import { getBaseUrl } from './baseUrl';
import type { RemoteTroubleshootingPlan } from '../types';
import type { PortalMessageAttachment, PortalMessageChannel, PortalMessageFocus } from '../utils/portalMessages';

async function json(res: Response) {
  if (!res.ok) {
    let msg = 'Request failed';
    try {
      const e = await res.json();
      const base = e.error || e.message || msg;
      const details = typeof e.details === 'string' ? e.details.trim() : '';
      msg = details ? `${base} ${details}` : base;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export const deviceApi = {
  async getModels(options: { compact?: boolean } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.compact) params.set('compact', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/device-models${query ? `?${query}` : ''}`));
  },
  async getAll(options: { includeInactive?: boolean; modelId?: string; full?: boolean; selector?: boolean } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.includeInactive) params.set('includeInactive', '1');
    if (options.modelId) params.set('modelId', options.modelId);
    if (options.full) params.set('full', '1');
    if (options.selector) params.set('selector', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/devices${query ? `?${query}` : ''}`));
  },
  async getForModel(modelId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/device-models/${encodeURIComponent(modelId)}/devices`));
  },
  async getOrgModelDeviceSummary(modelId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/device-models/${encodeURIComponent(modelId)}/org-device-summary`));
  },
  async getBySerial(serial: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/devices/serial/${encodeURIComponent(serial)}`));
  },
  async resolveQrToken(token: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/devices/qr/${encodeURIComponent(token)}`));
  },
  async ensureQrToken(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices/${encodeURIComponent(id)}/qr-token`, {
        method: 'POST'
      })
    );
  },
  async create(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async assign(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices/${id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async release(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices/${id}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async update(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async getSlaAssignment(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/devices/${encodeURIComponent(id)}/sla-assignment`));
  },
  async saveSlaAssignment(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/devices/${encodeURIComponent(id)}/sla-assignment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getSlaReport(id: string, options: { assignmentId?: string | null } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.assignmentId) params.set('assignmentId', options.assignmentId);
    const query = params.toString();
    return json(
      await fetch(
        `${b}/api/devices/${encodeURIComponent(id)}/sla-report${query ? `?${query}` : ''}`
      )
    );
  },
  async getDeviceTypes() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/device-types`));
  },
  async generateDeviceDescription(body: {
    modelName: string;
    modelNumber: string;
    deviceType: string;
    manufacturer?: string;
    technologySummary?: string;
    existingDescription?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/device-models/description/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  }
};

export const customerApi = {
  async getAll(options: { full?: boolean; locations?: boolean } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.full) params.set('full', '1');
    if (options.locations) params.set('locations', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/customers${query ? `?${query}` : ''}`));
  },
  async getById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/customers/${encodeURIComponent(id)}`));
  },
  async create(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async update(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async remove(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/customers/${id}`, { method: 'DELETE' }));
  },
  async addLocation(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async updateLocation(id: string, locId: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/locations/${locId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async removeLocation(id: string, locId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/customers/${id}/locations/${locId}`, { method: 'DELETE' }));
  },
  async addContact(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async updateContact(id: string, contactId: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/contacts/${contactId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async removeContact(id: string, contactId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/customers/${id}/contacts/${contactId}`, { method: 'DELETE' }));
  },
  async invitePortalUser(
    id: string,
    contactId: string,
    body: { resetCredentials?: boolean; portalAccessMode?: 'FULL' | 'LITE' } = {}
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/contacts/${contactId}/portal-user/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async setPortalUserActive(id: string, contactId: string, isActive: boolean) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/customers/${id}/contacts/${contactId}/portal-user`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive })
      })
    );
  }
};

export const slaTemplateApi = {
  async getAll() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/sla-templates`));
  },
  async getById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/sla-templates/${encodeURIComponent(id)}`));
  },
  async getReport(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/sla-templates/${encodeURIComponent(id)}/report`));
  },
  async generateReport(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/sla-templates/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async saveReport(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/sla-templates/${encodeURIComponent(id)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async create(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/sla-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async update(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/sla-templates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  }
};

export const messageApi = {
  async getAll(options: {
    focusType?: PortalMessageFocus | 'all';
    channelType?: PortalMessageChannel;
    view?: 'active' | 'archived' | 'all';
    customerId?: string;
    caseId?: string;
    deviceId?: string;
    unreadOnly?: boolean;
    includeSent?: boolean;
    includeReadAudit?: boolean;
  } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.focusType && options.focusType !== 'all') params.set('focusType', options.focusType);
    if (options.channelType) params.set('channelType', options.channelType);
    if (options.view) params.set('view', options.view);
    if (options.customerId) params.set('customerId', options.customerId);
    if (options.caseId) params.set('caseId', options.caseId);
    if (options.deviceId) params.set('deviceId', options.deviceId);
    if (options.unreadOnly) params.set('unreadOnly', '1');
    if (options.includeSent === false) params.set('includeSent', '0');
    if (options.includeReadAudit) params.set('includeReadAudit', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/messages${query ? `?${query}` : ''}`));
  },
  async create(body: {
    body: string;
    focusType: PortalMessageFocus;
    channelType?: PortalMessageChannel;
    customerId?: string;
    caseId?: string;
    deviceId?: string;
    recipientUserId?: string;
    attachments?: PortalMessageAttachment[];
  }) {
    const b = await getBaseUrl();
    const attachments = Array.isArray(body?.attachments) ? body.attachments : [];
    const attachmentFiles = attachments
      .map(item => item?.file)
      .filter((file): file is File => typeof File !== 'undefined' && file instanceof File);

    if (attachmentFiles.length > 0) {
      const form = new FormData();
      form.append('body', String(body?.body || ''));
      form.append('focusType', String(body?.focusType || 'general'));
      if (body?.channelType) form.append('channelType', body.channelType);
      if (body?.customerId) form.append('customerId', body.customerId);
      if (body?.caseId) form.append('caseId', body.caseId);
      if (body?.deviceId) form.append('deviceId', body.deviceId);
      if (body?.recipientUserId) form.append('recipientUserId', body.recipientUserId);
      for (const file of attachmentFiles) {
        form.append('files', file, file.name);
      }
      return json(
        await fetch(`${b}/api/messages`, {
          method: 'POST',
          body: form
        })
      );
    }

    return json(
      await fetch(`${b}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async markRead(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/messages/${encodeURIComponent(id)}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async archive(id: string, archived: boolean = true) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/messages/${encodeURIComponent(id)}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived })
      })
    );
  },
  async archiveThread(threadId: string, archived: boolean = true) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/messages/threads/${encodeURIComponent(threadId)}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived })
      })
    );
  },
  async deleteForMe(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/messages/${encodeURIComponent(id)}/delete-for-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async restoreForMe(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/messages/${encodeURIComponent(id)}/restore-for-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async getInternalContacts() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/messages/internal-contacts`));
  },
  async getUnreadSummary(): Promise<{ external: number; internal: number }> {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/messages/unread-summary`));
  },
  async downloadAttachment(messageId: string, attachmentId: string, fileName?: string) {
    const b = await getBaseUrl();
    const response = await fetch(
      `${b}/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}/download`
    );
    if (!response.ok) {
      let msg = 'Failed to download attachment';
      try {
        const payload = await response.json();
        msg = payload?.error || msg;
      } catch {}
      throw new Error(msg);
    }
    const blob = await response.blob();
    const downloadName = String(fileName || 'attachment.bin').trim() || 'attachment.bin';
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }
};

export const notificationApi = {
  async getMine(options: { view?: 'active' | 'all' | 'dismissed' | 'unread'; take?: number } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.view) params.set('view', options.view);
    if (options.take) params.set('take', String(options.take));
    const query = params.toString();
    return json(await fetch(`${b}/api/notifications/mine${query ? `?${query}` : ''}`));
  },
  async getUnread(options: { take?: number } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.take) params.set('take', String(options.take));
    const query = params.toString();
    return json(await fetch(`${b}/api/notifications/unread${query ? `?${query}` : ''}`));
  },
  async markRead(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/notifications/${encodeURIComponent(id)}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async dismiss(id: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/notifications/${encodeURIComponent(id)}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async previewTargets(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/notifications/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getOrgUnits() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/notifications/org/units`));
  },
  async previewOrgTargets(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/notifications/org/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async createOrgCampaign(body: any) {
    const b = await getBaseUrl();
    const attachments = Array.isArray(body?.attachments) ? body.attachments : [];
    const attachmentFiles = attachments
      .map((item: PortalMessageAttachment) => item?.file)
      .filter((file: File | undefined): file is File => typeof File !== 'undefined' && file instanceof File);

    if (attachmentFiles.length > 0) {
      const form = new FormData();
      Object.entries(body || {}).forEach(([key, value]) => {
        if (key === 'attachments' || value === undefined || value === null) return;
        form.append(key, Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
      for (const file of attachmentFiles) {
        form.append('files', file, file.name);
      }
      return json(
        await fetch(`${b}/api/notifications/org/campaigns`, {
          method: 'POST',
          body: form
        })
      );
    }

    return json(
      await fetch(`${b}/api/notifications/org/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getOrgCampaigns() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/notifications/org/campaigns`));
  },
  async getOrgCampaign(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/notifications/org/campaigns/${encodeURIComponent(id)}`));
  },
  async fetchOrgCampaignAttachment(campaignId: string, attachmentId: string) {
    const b = await getBaseUrl();
    const response = await fetch(
      `${b}/api/notifications/org/campaigns/${encodeURIComponent(campaignId)}/attachments/${encodeURIComponent(
        attachmentId
      )}/download`
    );
    if (!response.ok) {
      let msg = 'Failed to load attachment';
      try {
        const payload = await response.json();
        msg = payload?.error || msg;
      } catch {}
      throw new Error(msg);
    }
    const blob = await response.blob();
    return {
      blob,
      url: URL.createObjectURL(blob),
      contentType: response.headers.get('Content-Type') || blob.type || 'application/octet-stream'
    };
  },
  async openOrgCampaignAttachment(campaignId: string, attachmentId: string, fileName?: string) {
    const file = await notificationApi.fetchOrgCampaignAttachment(campaignId, attachmentId);
    const opened = window.open(file.url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = fileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    window.setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
    return file;
  },
  async createCampaign(body: any) {
    const b = await getBaseUrl();
    const attachments = Array.isArray(body?.attachments) ? body.attachments : [];
    const attachmentFiles = attachments
      .map((item: PortalMessageAttachment) => item?.file)
      .filter((file: File | undefined): file is File => typeof File !== 'undefined' && file instanceof File);

    if (attachmentFiles.length > 0) {
      const form = new FormData();
      Object.entries(body || {}).forEach(([key, value]) => {
        if (key === 'attachments' || value === undefined || value === null) return;
        form.append(key, String(value));
      });
      for (const file of attachmentFiles) {
        form.append('files', file, file.name);
      }
      return json(
        await fetch(`${b}/api/notifications/campaigns`, {
          method: 'POST',
          body: form
        })
      );
    }

    return json(
      await fetch(`${b}/api/notifications/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getCampaigns() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/notifications/campaigns`));
  },
  async fetchAttachment(notificationId: string, attachmentId: string) {
    const b = await getBaseUrl();
    const response = await fetch(
      `${b}/api/notifications/${encodeURIComponent(notificationId)}/attachments/${encodeURIComponent(attachmentId)}/download`
    );
    if (!response.ok) {
      let msg = 'Failed to load attachment';
      try {
        const payload = await response.json();
        msg = payload?.error || msg;
      } catch {}
      throw new Error(msg);
    }
    const blob = await response.blob();
    return {
      blob,
      url: URL.createObjectURL(blob),
      contentType: response.headers.get('Content-Type') || blob.type || 'application/octet-stream'
    };
  },
  async openAttachment(notificationId: string, attachmentId: string, fileName?: string) {
    const b = await getBaseUrl();
    const response = await fetch(
      `${b}/api/notifications/${encodeURIComponent(notificationId)}/attachments/${encodeURIComponent(attachmentId)}/download`
    );
    if (!response.ok) {
      let msg = 'Failed to open attachment';
      try {
        const payload = await response.json();
        msg = payload?.error || msg;
      } catch {}
      throw new Error(msg);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const downloadName = String(fileName || 'attachment.bin').trim() || 'attachment.bin';
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }
};

type RemotePlanRequest = {
  caseId?: string;
  deviceModel: string;
  problemDescription?: string;
  symptoms?: string[];
  customerInputs?: unknown[];
};

type RemotePlanResponse = {
  modelId: string;
  modelNumber: string;
  remotePlan: RemoteTroubleshootingPlan;
};

export const tsfadApi = {
  async getRemotePlan(body: RemotePlanRequest): Promise<RemotePlanResponse> {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/remote-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    return json(res);
  },
  async acceptAnalysis(
    analysisId: string,
    body: { caseId: string; possibleCauses?: unknown[]; clientCaseModel?: Record<string, unknown> }
  ) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    return json(res);
  },
  async listQuotes(analysisId: string) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes`);
    return json(res);
  },
  async getCauseDetails(analysisId: string, causeId: string) {
    const b = await getBaseUrl();
    const res = await fetch(
      `${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/cause/${encodeURIComponent(causeId)}`
    );
    return json(res);
  },
  async optimizePlan(analysisId: string) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/optimize-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analysisId })
    });
    return json(res);
  },
  async saveQuote(
    analysisId: string,
    body: {
      quoteId?: string;
      revisionOfQuoteId?: string;
      templateId?: string;
      title: string;
      description?: string;
      lineItems: Array<{
        id?: string;
        lineType: 'part' | 'labor';
        description: string;
        quantity: number;
        unitValue: number;
        isFree?: boolean;
        source?: string;
        catalogId?: string | null;
        sku?: string | null;
        partNumber?: string | null;
        originalUnitValue?: number | null;
        slaCoverageStatus?: 'COVERED' | 'DISCOUNTED' | null;
        slaLabel?: string | null;
        slaDiscountPercent?: number | null;
        slaCoverageReason?: string | null;
        slaMatchedListNames?: string[];
      }>;
      notes?: string;
      customerApprovalName?: string;
      customerApprovalSignature?: string;
      unmatchedParts?: string[];
      totalSettings?: unknown;
      responseActions?: string[];
      approvalPlacement?: string;
      approvalFooterSlots?: string[];
      requireSignature?: boolean;
      signatureLabel?: string;
      requireTechnicianSignature?: boolean;
      technicianSignatureLabel?: string;
    }
  ) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    });
    return json(res);
  },
  async respondQuote(
    analysisId: string,
    quoteId: string,
    body: {
      decision: 'ACCEPT' | 'DENY' | 'ADJUST';
      note?: string;
      actorName?: string;
      customerApprovalName?: string;
      customerApprovalSignature?: string;
    }
  ) {
    const b = await getBaseUrl();
    const res = await fetch(
      `${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes/${encodeURIComponent(quoteId)}/respond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      }
    );
    return json(res);
  },
  async sendQuote(analysisId: string, quoteId: string) {
    const b = await getBaseUrl();
    const res = await fetch(
      `${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes/${encodeURIComponent(quoteId)}/send`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }
    );
    return json(res);
  },
  async setQuoteActive(
    analysisId: string,
    quoteId: string,
    body: { active: boolean; reason?: string }
  ) {
    const b = await getBaseUrl();
    const res = await fetch(
      `${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes/${encodeURIComponent(quoteId)}/active`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      }
    );
    return json(res);
  },
  async deleteQuote(analysisId: string, quoteId: string) {
    const b = await getBaseUrl();
    const res = await fetch(
      `${b}/api/tsfad/analysis/${encodeURIComponent(analysisId)}/quotes/${encodeURIComponent(quoteId)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      }
    );
    return json(res);
  },
  async submitDocumentationFeedback(body: {
    modelId: string;
    caseId?: string | null;
    documentId?: string | null;
    documentSectionId?: string | null;
    feedbackType: string;
    severity: string;
    title: string;
    description: string;
    suggestedFix?: string | null;
    impact?: string | null;
    technicianId?: string | null;
    technicianName?: string | null;
    metadata?: Record<string, unknown> | null;
  }) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/tsfad/documentation-feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    return json(res);
  }
};

export const supportQuestionsApi = {
  async generate(body: {
    device: {
      modelNumber?: string;
      manufacturer?: string;
      modelName?: string;
      deviceType?: string;
      serialNumber?: string;
      description?: string;
    };
    problemDescription: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/support-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  }
};

export const caseApi = {
  async getAll(options: { status?: string; needsVerification?: boolean; deviceId?: string; summary?: boolean } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (typeof options.needsVerification === 'boolean') {
      params.set('needsVerification', options.needsVerification ? 'true' : 'false');
    }
    if (options.deviceId) params.set('deviceId', options.deviceId);
    if (options.summary) params.set('summary', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/cases${query ? `?${query}` : ''}`));
  },
  async getById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/cases/${encodeURIComponent(id)}`));
  },
  async getSurveys() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/cases/portal-surveys`));
  },
  async getSurveyById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/cases/portal-surveys/${encodeURIComponent(id)}`));
  },
  async submitSurvey(
    id: string,
    body: {
      commentsText?: string | null;
      answers: Array<{
        questionKey: string;
        rawScore: number;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/portal-surveys/${encodeURIComponent(id)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getSurveyResponseDetail(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/cases/surveys/${encodeURIComponent(id)}/responses`));
  },
  async getTsfadSummary(id: string) {
    const b = await getBaseUrl();
    const encodedId = encodeURIComponent(id);
    try {
      return await json(await fetch(`${b}/api/cases/${encodedId}/tsfad-summary`));
    } catch (err) {
      console.warn('TSFAD summary endpoint failed, falling back to full case payload:', err);
      return json(await fetch(`${b}/api/cases/${encodedId}`));
    }
  },
  async updateStatus(
    id: string,
    status: string,
    options: {
      source?: 'manual' | 'task' | 'system' | string;
      reason?: string | null;
      triggerTaskId?: string | null;
    } = {}
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          source: options.source,
          reason: options.reason,
          triggerTaskId: options.triggerTaskId
        })
      })
    );
  },
  async updatePriority(id: string, priority: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/${id}/priority`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority })
      })
    );
  },
  async updateProblem(id: string, body: { summary?: string; details?: string[]; problemDescription?: string }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/${id}/problem`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
    );
  },
  async getAssignees(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/cases/${encodeURIComponent(id)}/assignees`));
  },
  async updateTaskSchedule(taskId: string, body: { dayKey: string | null }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/tasks/${encodeURIComponent(taskId)}/schedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async requestTaskSchedule(
    taskId: string,
    body: { requestedStart: string; requestedDue: string }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/tasks/${encodeURIComponent(taskId)}/schedule-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async confirmTaskSchedule(taskId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/tasks/${encodeURIComponent(taskId)}/schedule-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async getSafetyWorkflowWorkspace(caseId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade`));
  },
  async selectSafetyWorkflow(caseId: string, workflowProtocolId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflowProtocolId })
      })
    );
  },
  async generateSafetyWorkflow(caseId: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async saveSafetyWorkflowSnapshot(caseId: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async updateSafetyWorkflowSnapshot(caseId: string, snapshotId: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/snapshot/${encodeURIComponent(snapshotId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body || {})
        }
      )
    );
  },
  async generateSafetyWorkflowSummary(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      )
    );
  },
  async saveSafetyWorkflowSummary(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createSafetyWorkflowQuoteDraft(caseId: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quote/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async saveSafetyWorkflowQuote(caseId: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quote/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async listSafetyWorkflowQuotes(caseId: string, options: { snapshotId?: string } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.snapshotId) params.set('snapshotId', options.snapshotId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quotes${suffix}`)
    );
  },
  async sendSafetyWorkflowQuote(
    caseId: string,
    quoteId: string,
    body: { autoRequestTransfer?: boolean } = {}
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quotes/${encodeURIComponent(quoteId)}/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body || {})
        }
      )
    );
  },
  async setSafetyWorkflowQuoteActive(
    caseId: string,
    quoteId: string,
    body: { active: boolean; reason?: string }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quotes/${encodeURIComponent(quoteId)}/active`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body || {})
        }
      )
    );
  },
  async deleteSafetyWorkflowQuote(caseId: string, quoteId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quotes/${encodeURIComponent(quoteId)}`,
        {
          method: 'DELETE'
        }
      )
    );
  },
  async respondSafetyWorkflowQuote(
    caseId: string,
    quoteId: string,
    body: {
      decision: 'ACCEPT' | 'DENY' | 'ADJUST';
      note?: string;
      customerApprovalName?: string;
      customerApprovalSignature?: string;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/quotes/${encodeURIComponent(quoteId)}/respond`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body || {})
        }
      )
    );
  },
  async createSafetyWorkflowServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveSafetyWorkflowServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendSafetyWorkflowServiceReport(caseId: string, reportId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/case-workflows/${encodeURIComponent(caseId)}/safety-upgrade/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })
      })
    );
  },
  async updateWorkflow(
    id: string,
    body: {
      taskGroup?: string;
      status?: string;
      priority?: string;
      assigneeUserIds?: string[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/${encodeURIComponent(id)}/workflow`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async verifyModel(
    id: string,
    body: {
      action: 'approve' | 'edit' | 'reject';
      modelNumber?: string;
      manufacturer?: string | null;
      serialNumber?: string;
      assetNumber?: string | null;
      locationId?: string | null;
      deviceId?: string;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/cases/${encodeURIComponent(id)}/verify-model`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async submit(formData: FormData) {
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/cases/submit`, { method: 'POST', body: formData });
    return json(res);
  }
};

export const scheduleApi = {
  async getPlannerUsers() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/scheduling/planner-users`, { cache: 'no-store' }));
  },
  async getCurrentCaseSchedule(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/scheduling/cases/${encodeURIComponent(caseId)}/current`, { cache: 'no-store' })
    );
  },
  async getCurrentPlans() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/scheduling/plans/current`, { cache: 'no-store' }));
  },
  async runSmartPlanner(body: {
    horizonStartAt: string;
    horizonEndAt: string;
    autoScheduleStartDayKey: string;
    plannerUserIds?: string[];
    conditions?: Record<string, unknown>;
    options?: Record<string, boolean>;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/scheduling/plans/smart-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async saveDraftPlan(body: {
    name?: string;
    planningMode?: string;
    horizonStartAt: string;
    horizonEndAt: string;
    conditions?: Record<string, unknown>;
    items: Array<{
      caseId: string;
      taskId: string;
      assignedDayKey: string;
      scheduledUserId?: string | null;
      scheduledStartAt?: string;
      scheduledEndAt?: string;
      source?: string;
      sortOrder?: number;
    }>;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/scheduling/plans/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async publishPlan(body: {
    name?: string;
    planningMode?: string;
    horizonStartAt: string;
    horizonEndAt: string;
    conditions?: Record<string, unknown>;
    items: Array<{
      caseId: string;
      taskId: string;
      assignedDayKey: string;
      scheduledUserId?: string | null;
      scheduledStartAt?: string;
      scheduledEndAt?: string;
      source?: string;
      sortOrder?: number;
    }>;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/scheduling/plans/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async notifyPlanUsers() {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/scheduling/plans/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  }
};

export const internalJobApi = {
  async getAll() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/internal-jobs`));
  },
  async getById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/internal-jobs/${encodeURIComponent(id)}`));
  },
  async create(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/internal-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async preview(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/internal-jobs/${encodeURIComponent(id)}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async previewDevices(body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/internal-jobs/preview-devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async generate(id: string, body: any) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/internal-jobs/${encodeURIComponent(id)}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getCases(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/internal-jobs/${encodeURIComponent(id)}/cases`));
  },
  async uploadAttachments(files: File[]) {
    const b = await getBaseUrl();
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return json(
      await fetch(`${b}/api/internal-jobs/attachments`, {
        method: 'POST',
        body: formData
      })
    );
  }
};

export const repairApi = {
  async get(caseId: string, options: { analysisId?: string } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.analysisId) {
      params.set('analysisId', options.analysisId);
    }
    const query = params.toString();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}${query ? `?${query}` : ''}`)
    );
  },
  async save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      steps: any[];
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      taskStatus?: string;
      suppressReport?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async scanPartLabel(imageFile: File) {
    const b = await getBaseUrl();
    const formData = new FormData();
    formData.append('image', imageFile);
    const res = await fetch(`${b}/api/repair/parts/scan`, {
      method: 'POST',
      body: formData
    });
    return json(res);
  },
  async createStep(
    caseId: string,
    payload: {
      runId: string;
      label: string;
      summary?: string;
      probability?: string;
      verificationSteps?: string[];
      repairSteps?: string[];
      requiredTools?: string[];
      requiredParts?: string[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async getFunctionalTestReference(caseId: string, type: 'MFTP' | 'CFTP') {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/repair/${encodeURIComponent(caseId)}/functional-test-reference/${encodeURIComponent(type)}`
      )
    );
  },
  async refresh(caseId: string, payload: { analysisId?: string } = {}) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async generateReport(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveReport(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendServiceReport(caseId: string, reportId?: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportId ? { reportId } : {})
      })
    );
  },
  async clear(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/repair/${encodeURIComponent(caseId)}/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  }
};

const maintenanceVariantPath = (variant: 'checkup' | 'preventative') =>
  variant === 'preventative' ? 'preventative' : 'checkup';

async function maintenanceRequest<T>(
  method: 'GET' | 'POST' | 'PUT',
  caseId: string,
  variant: 'checkup' | 'preventative',
  suffix: string,
  body?: any
): Promise<T> {
  const b = await getBaseUrl();
  const basePath = `${b}/api/maintenance/${encodeURIComponent(caseId)}/${maintenanceVariantPath(
    variant
  )}${suffix}`;
  const url =
    method === 'GET' && body && typeof body === 'object' && Object.keys(body).length > 0
      ? `${basePath}?${new URLSearchParams(
          Object.entries(body)
            .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
            .map(([key, value]) => [key, String(value)])
        ).toString()}`
      : basePath;
  return json(
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: method === 'GET' ? undefined : body ? JSON.stringify(body) : undefined
    })
  );
}

export const maintenanceApi = {
  getCheckup(caseId: string, options: { interval?: string } = {}) {
    return maintenanceRequest('GET', caseId, 'checkup', '', options);
  },
  saveCheckup(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    return maintenanceRequest('POST', caseId, 'checkup', '/save', payload);
  },
  refreshCheckup(caseId: string, options: { interval?: string; revisionId?: string } = {}) {
    return maintenanceRequest('POST', caseId, 'checkup', '/refresh', options);
  },
  clearCheckup(caseId: string, options: { interval?: string } = {}) {
    return maintenanceRequest('POST', caseId, 'checkup', '/clear', options);
  },
  generateCheckupReport(caseId: string) {
    return maintenanceRequest<{ summary: string; generatedAt: string; isCustom: boolean }>(
      'POST',
      caseId,
      'checkup',
      '/report'
    );
  },
  saveCheckupReport(caseId: string, summary: string) {
    return maintenanceRequest<{ summary: string; generatedAt: string; isCustom: boolean }>(
      'PUT',
      caseId,
      'checkup',
      '/report',
      { summary }
    );
  },
  createCheckupServiceReportDraft(caseId: string) {
    return maintenanceRequest('POST', caseId, 'checkup', '/service-report/draft');
  },
  saveCheckupServiceReport(caseId: string, document: Record<string, unknown>) {
    return maintenanceRequest('PUT', caseId, 'checkup', '/service-report', { document });
  },
  sendCheckupServiceReport(caseId: string, reportId: string) {
    return maintenanceRequest('POST', caseId, 'checkup', '/service-report/send', { reportId });
  },
  getPreventative(caseId: string, options: { interval?: string } = {}) {
    return maintenanceRequest('GET', caseId, 'preventative', '', options);
  },
  savePreventative(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    return maintenanceRequest('POST', caseId, 'preventative', '/save', payload);
  },
  refreshPreventative(
    caseId: string,
    options: { interval?: string; revisionId?: string } = {}
  ) {
    return maintenanceRequest('POST', caseId, 'preventative', '/refresh', options);
  },
  clearPreventative(caseId: string, options: { interval?: string } = {}) {
    return maintenanceRequest('POST', caseId, 'preventative', '/clear', options);
  },
  generatePreventativeReport(caseId: string) {
    return maintenanceRequest<{ summary: string; generatedAt: string; isCustom: boolean }>(
      'POST',
      caseId,
      'preventative',
      '/report'
    );
  },
  savePreventativeReport(caseId: string, summary: string) {
    return maintenanceRequest<{ summary: string; generatedAt: string; isCustom: boolean }>(
      'PUT',
      caseId,
      'preventative',
      '/report',
      { summary }
    );
  },
  createPreventativeServiceReportDraft(caseId: string) {
    return maintenanceRequest('POST', caseId, 'preventative', '/service-report/draft');
  },
  savePreventativeServiceReport(caseId: string, document: Record<string, unknown>) {
    return maintenanceRequest('PUT', caseId, 'preventative', '/service-report', { document });
  },
  sendPreventativeServiceReport(caseId: string, reportId: string) {
    return maintenanceRequest('POST', caseId, 'preventative', '/service-report/send', { reportId });
  }
};

export const installPrepApi = {
  async get(caseId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}`));
  },
  async uploadAttachment(caseId: string, stepId: string, file: File) {
    const b = await getBaseUrl();
    const form = new FormData();
    form.append('file', file);
    return json(
      await fetch(
        `${b}/api/install-prep/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments`,
        {
          method: 'POST',
          body: form
        }
      )
    );
  },
  async removeAttachment(caseId: string, stepId: string, attachmentId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/install-prep/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' }
      )
    );
  },
  async save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async refresh(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async generateReport(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveReport(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendServiceReport(caseId: string, reportId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-prep/${encodeURIComponent(caseId)}/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })
      })
    );
  }
};

export const installExecutionApi = {
  async get(caseId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}`));
  },
  async uploadAttachment(caseId: string, stepId: string, file: File) {
    const b = await getBaseUrl();
    const form = new FormData();
    form.append('file', file);
    return json(
      await fetch(
        `${b}/api/install-execution/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments`,
        {
          method: 'POST',
          body: form
        }
      )
    );
  },
  async removeAttachment(caseId: string, stepId: string, attachmentId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/install-execution/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' }
      )
    );
  },
  async save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async refresh(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async generateReport(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveReport(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendServiceReport(caseId: string, reportId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-execution/${encodeURIComponent(caseId)}/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })
      })
    );
  }
};

export const installVerificationApi = {
  async get(caseId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}`));
  },
  async uploadAttachment(caseId: string, stepId: string, file: File) {
    const b = await getBaseUrl();
    const form = new FormData();
    form.append('file', file);
    return json(
      await fetch(
        `${b}/api/install-verification/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments`,
        {
          method: 'POST',
          body: form
        }
      )
    );
  },
  async removeAttachment(caseId: string, stepId: string, attachmentId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/install-verification/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' }
      )
    );
  },
  async save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async refresh(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async generateReport(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveReport(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendServiceReport(caseId: string, reportId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-verification/${encodeURIComponent(caseId)}/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })
      })
    );
  }
};

export const installDecommissionApi = {
  async get(caseId: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}`));
  },
  async uploadAttachment(caseId: string, stepId: string, file: File) {
    const b = await getBaseUrl();
    const form = new FormData();
    form.append('file', file);
    return json(
      await fetch(
        `${b}/api/install-decommission/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments`,
        {
          method: 'POST',
          body: form
        }
      )
    );
  },
  async removeAttachment(caseId: string, stepId: string, attachmentId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/install-decommission/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' }
      )
    );
  },
  async save(
    caseId: string,
    payload: {
      taskId: string;
      runId: string;
      taskStatus?: string;
      runNotes?: string;
      runMetadata?: Record<string, unknown>;
      steps: any[];
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {})
      })
    );
  },
  async refresh(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async generateReport(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveReport(caseId: string, summary: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary })
      })
    );
  },
  async createServiceReportDraft(caseId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/service-report/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
    );
  },
  async saveServiceReport(caseId: string, document: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/service-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document })
      })
    );
  },
  async sendServiceReport(caseId: string, reportId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/install-decommission/${encodeURIComponent(caseId)}/service-report/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })
      })
    );
  }
};

export const referenceApi = {
  async listActivations(modelId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/reference-docs/${encodeURIComponent(modelId)}/activations`)
    );
  },
  async setActivation(modelId: string, checklistType: string, isActive: boolean) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/activation`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isActive })
        }
      )
    );
  },
  async get(
    modelId: string,
    checklistType: string,
    options?: string | { revisionId?: string; interval?: string }
  ) {
    const b = await getBaseUrl();
    let revisionId = '';
    let interval = '';
    if (typeof options === 'string') {
      revisionId = options;
    } else if (options && typeof options === 'object') {
      revisionId = String(options.revisionId || '').trim();
      interval = String(options.interval || '').trim();
    }
    const params = new URLSearchParams();
    if (revisionId) params.set('revisionId', revisionId);
    if (interval) params.set('interval', interval);
    const query = params.toString() ? `?${params.toString()}` : '';
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(
          checklistType
        )}${query}`
      )
    );
  },
  async regenerate(modelId: string, checklistType: string, options?: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/regenerate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(options || {})
        }
      )
    );
  },
  async list(modelId: string, checklistType: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/revisions`
      )
    );
  },
  async listReferenceKits(modelId: string, checklistType: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/kits`
      )
    );
  },
  async setReferenceKit(modelId: string, checklistType: string, kitId?: string | null) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/kit`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kitId: kitId || null })
        }
      )
    );
  },
  async setDefault(modelId: string, checklistType: string, revisionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(
          checklistType
        )}/${encodeURIComponent(revisionId)}/default`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      )
    );
  },
  async remove(modelId: string, checklistType: string, revisionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(
          checklistType
        )}/${encodeURIComponent(revisionId)}`,
        {
          method: 'DELETE'
        }
      )
    );
  },
  async saveManual(
    modelId: string,
    checklistType: string,
    payload: {
      steps: any[];
      sourceCaseId?: string | null;
      baseRevisionId?: string | null;
      maintenanceInterval?: string | null;
      estimatedDurationMinutes?: number | null;
      setAsDefault?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/reference-docs/${encodeURIComponent(modelId)}/${encodeURIComponent(checklistType)}/manual`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload || {})
        }
      )
    );
  }
};

export const formsApi = {
  async getAll() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/forms`));
  },
  async saveAll(body: {
    surveys: any[];
    reports: any[];
    quotes: any[];
    invoices: any[];
    technicalForms?: any[];
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/forms`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getGeneratedTechnicalDocuments(options: { type?: string } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.type) params.set('type', options.type);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return json(await fetch(`${b}/api/forms/technical-documents${suffix}`));
  },
  async createGeneratedTechnicalDocument(body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/forms/technical-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getGeneratedTechnicalDocumentBlob(id: string) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Generated document id is required');
    const b = await getBaseUrl();
    const res = await fetch(`${b}/api/forms/technical-documents/${encodeURIComponent(cleanId)}/view`);
    if (!res.ok) {
      let msg = 'Failed to open generated technical document.';
      try {
        const e = await res.json();
        msg = e.error || e.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return res.blob();
  }
};

export const workflowProtocolsApi = {
  async list(options: { status?: string; category?: string; deviceModelId?: string } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.category) params.set('category', options.category);
    if (options.deviceModelId) params.set('deviceModelId', options.deviceModelId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return json(await fetch(`${b}/api/workflow-protocols${suffix}`));
  },
  async create(body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/workflow-protocols`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async generate(file: File, body: Record<string, unknown> = {}) {
    const b = await getBaseUrl();
    const form = new FormData();
    form.append('source', file);
    Object.entries(body || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      form.append(key, String(value));
    });
    return json(
      await fetch(`${b}/api/workflow-protocols/generate`, {
        method: 'POST',
        body: form
      })
    );
  },
  async update(id: string, body: Record<string, unknown>) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Workflow id is required');
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/workflow-protocols/${encodeURIComponent(cleanId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async redraft(id: string, body: Record<string, unknown> = {}) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Workflow id is required');
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/workflow-protocols/${encodeURIComponent(cleanId)}/redraft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async remove(id: string) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Workflow id is required');
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/workflow-protocols/${encodeURIComponent(cleanId)}`, {
        method: 'DELETE'
      })
    );
  },
  async getArtifact(id: string) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Workflow id is required');
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/workflow-protocols/${encodeURIComponent(cleanId)}/artifact`));
  },
  async getSourceBlob(id: string) {
    const cleanId = String(id || '').trim();
    if (!cleanId) throw new Error('Workflow id is required');
    const b = await getBaseUrl();
    const response = await fetch(`${b}/api/workflow-protocols/${encodeURIComponent(cleanId)}/source`);
    if (!response.ok) {
      let message = `Request failed (${response.status})`;
      try {
        const payload = await response.json();
        message = payload?.error || message;
      } catch {
        // keep fallback
      }
      throw new Error(message);
    }
    return response.blob();
  }
};

export const pricingApi = {
  async getAll() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/pricing`));
  },
  async saveAll(body: { items: any[] }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async updateItem(id: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async uploadFile(file: File) {
    const b = await getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    return json(
      await fetch(`${b}/api/pricing/upload`, {
        method: 'POST',
        body: formData
      })
    );
  },
  async getFailureRates() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/pricing/failure-rates`));
  },
  async saveFailureRates(body: { items: any[] }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/failure-rates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async uploadFailureRatesFile(file: File) {
    const b = await getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    return json(
      await fetch(`${b}/api/pricing/upload/failure-rates`, {
        method: 'POST',
        body: formData
      })
    );
  },
  async updateFailureRateItem(id: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/failure-rates/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getKits() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/pricing/kits`));
  },
  async createKit(body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/kits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async updateKit(id: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/kits/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async uploadKitsFile(file: File) {
    const b = await getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    return json(
      await fetch(`${b}/api/pricing/upload/kits`, {
        method: 'POST',
        body: formData
      })
    );
  },
  async uploadKitComponentLinesFile(file: File) {
    const b = await getBaseUrl();
    const formData = new FormData();
    formData.append('file', file);
    return json(
      await fetch(`${b}/api/pricing/upload/kits/components`, {
        method: 'POST',
        body: formData
      })
    );
  },
  async getSlaLists() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/pricing/sla-lists`));
  },
  async createSlaList(body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/sla-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async updateSlaList(id: string, body: Record<string, unknown>) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/pricing/sla-lists/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  }
};

export const inventoryApi = {
  async getWarehouses() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/inventory/warehouses`));
  },
  async createWarehouse(body: {
    name: string;
    code?: string;
    warehouseType?: 'SERVICE' | 'GENERAL' | 'CUSTOMER';
    isPrimary?: boolean;
    customerId?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async updateWarehouse(
    warehouseId: string,
    body: {
      name?: string;
      code?: string | null;
      isActive?: boolean;
      deactivationReason?: string;
      isPrimary?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getWarehouseStock(
    warehouseId: string,
    params?: {
      query?: string;
      page?: number;
      limit?: number;
      includeLots?: boolean;
      includeOnOrder?: boolean;
      includeEmpty?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (params?.query) qs.set('query', params.query);
    if (typeof params?.page === 'number') qs.set('page', String(params.page));
    if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
    if (typeof params?.includeLots === 'boolean') qs.set('includeLots', String(params.includeLots));
    if (typeof params?.includeOnOrder === 'boolean') qs.set('includeOnOrder', String(params.includeOnOrder));
    if (typeof params?.includeEmpty === 'boolean') qs.set('includeEmpty', String(params.includeEmpty));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/stock${suffix}`));
  },
  async getWarehouseStockLots(
    warehouseId: string,
    stockLevelId: string,
    params?: {
      includeEmpty?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (typeof params?.includeEmpty === 'boolean') qs.set('includeEmpty', String(params.includeEmpty));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(
      await fetch(
        `${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/stock/${encodeURIComponent(stockLevelId)}/lots${suffix}`
      )
    );
  },
  async getWarehouseKitBuildPreview(
    warehouseId: string,
    params: {
      kitId: string;
      quantity?: number;
    }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (params?.kitId) qs.set('kitId', params.kitId);
    if (typeof params?.quantity === 'number') qs.set('quantity', String(params.quantity));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(
      await fetch(
        `${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/kits/build-preview${suffix}`
      )
    );
  },
  async buildWarehouseKit(
    warehouseId: string,
    body: {
      kitId: string;
      quantity: number;
      destinationLocationId: string;
      packageLabel?: string;
      notes?: string;
      approvalConfirmed: boolean;
      componentSelections: Array<{
        componentLineId: string;
        sourceStockLevelId: string;
        sourceLotId?: string | null;
        quantity: number;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/kits/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getWarehouseKitBreakPreview(
    warehouseId: string,
    params: {
      kitId: string;
      quantity?: number;
    }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (params?.kitId) qs.set('kitId', params.kitId);
    if (typeof params?.quantity === 'number') qs.set('quantity', String(params.quantity));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(
      await fetch(
        `${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/kits/break-preview${suffix}`
      )
    );
  },
  async breakWarehouseKit(
    warehouseId: string,
    body: {
      kitId: string;
      quantity: number;
      sourceStockLevelId: string;
      sourceLotId?: string | null;
      defaultDestinationLocationId: string;
      notes?: string;
      approvalConfirmed: boolean;
      componentOutputs: Array<{
        componentLineId: string;
        destinationLocationId?: string;
        batchLotNumber?: string;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/kits/break`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getMovements(params?: {
    warehouseId?: string;
    itemId?: string;
    limit?: number;
    page?: number;
    query?: string;
    movementType?: string;
    referenceType?: string;
  }) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (params?.warehouseId) qs.set('warehouseId', params.warehouseId);
    if (params?.itemId) qs.set('itemId', params.itemId);
    if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
    if (typeof params?.page === 'number') qs.set('page', String(params.page));
    if (params?.query) qs.set('query', params.query);
    if (params?.movementType) qs.set('movementType', params.movementType);
    if (params?.referenceType) qs.set('referenceType', params.referenceType);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/movements${suffix}`));
  },
  async getWarehouseLocations(warehouseId: string, query?: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (query) qs.set('query', query);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations${suffix}`));
  },
  async createWarehouseLocation(
    warehouseId: string,
    body: {
      code: string;
      name?: string;
      isDefault?: boolean;
      isActive?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async removeWarehouseLocation(warehouseId: string, locationId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations/${encodeURIComponent(locationId)}`,
        {
          method: 'DELETE'
        }
      )
    );
  },
  async normalizeWarehouseLocations(warehouseId: string, body?: { itemIds?: string[] }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations/normalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getPlanningMinMax(warehouseId: string, query?: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (query) qs.set('query', query);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/planning/minmax${suffix}`));
  },
  async savePlanningMinMax(
    warehouseId: string,
    body: {
      rows: Array<{
        itemId: string;
        policyId?: string | null;
        minStockLevel?: number | null;
        maxStockLevel?: number | null;
        leadTimeDays?: number | null;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/planning/minmax`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getPlanningRecommendations(
    warehouseId: string,
    params?: { historyDays?: number; horizonDays?: number }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (typeof params?.historyDays === 'number') qs.set('historyDays', String(params.historyDays));
    if (typeof params?.horizonDays === 'number') qs.set('horizonDays', String(params.horizonDays));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/planning/recommendations${suffix}`)
    );
  },
  async uploadWarehouseStock(
    warehouseId: string,
    body: {
      mode?: 'set' | 'increment';
      rows: Array<{
        sku: string;
        partNumber?: string | null;
        description?: string | null;
        uom?: string | null;
        locationWh?: string | null;
        locationBin?: string | null;
        quantityOnHand: number;
        batchLotNumber?: string | null;
        expiryAt?: string | null;
        endOfLifeAt?: string | null;
        discontinuedAt?: string | null;
        replacementSku?: string | null;
        quantityOnOrder?: number | null;
        quantityInTransit?: number | null;
        expectedDeliveryAt?: string | null;
        leadTimeDays?: number | null;
        receivedAt?: string | null;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/stock/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async receiveStock(body: {
    warehouseId: string;
    sku: string;
    partNumber?: string | null;
    description: string;
    batchQty: number;
    locationCode?: string | null;
    batchLotNumber?: string | null;
    expiryAt?: string | null;
    endOfLifeAt?: string | null;
    discontinuedAt?: string | null;
    replacementSku?: string | null;
    notes?: string;
    referenceType?: string;
    referenceId?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/movements/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async shipStock(body: {
    warehouseId: string;
    stockLevelId: string;
    itemId: string;
    locationId: string;
    lotId?: string | null;
    shipQty: number;
    notes?: string;
    referenceType?: string;
    referenceId?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/movements/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async adjustStock(body: {
    warehouseId: string;
    stockLevelId: string;
    itemId: string;
    locationId: string;
    lotId?: string | null;
    adjustmentQty: number;
    notes?: string;
    referenceType?: string;
    referenceId?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/movements/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async createTransfer(body: {
    fromWarehouseId: string;
    toWarehouseId: string;
    notes?: string;
    referenceType?: string;
    referenceId?: string;
    lines: Array<{
      stockLevelId: string;
      itemId: string;
      locationId: string;
      lotId?: string | null;
      qty: number;
    }>;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getTransfers(warehouseId?: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (warehouseId) qs.set('warehouseId', warehouseId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/transfers${suffix}`));
  },
  async getPendingReplenishments(warehouseId?: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (warehouseId) qs.set('warehouseId', warehouseId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/replenishments/pending${suffix}`));
  },
  async getPendingActionCounts(warehouseId?: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (warehouseId) qs.set('warehouseId', warehouseId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(await fetch(`${b}/api/inventory/actions/pending${suffix}`));
  },
  async approveReplenishment(planId: string, body?: { notes?: string }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/replenishments/${encodeURIComponent(planId)}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async dispatchTransfer(
    transferId: string,
    body?: {
      notes?: string;
      referenceType?: string;
      referenceId?: string;
      lines?: Array<{
        lineId: string;
        qty: number;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/transfers/${encodeURIComponent(transferId)}/dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async approveTransferRequest(
    transferId: string,
    body?: {
      notes?: string;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/transfers/${encodeURIComponent(transferId)}/approve-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async receiveTransfer(
    transferId: string,
    body: {
      lines: Array<{
        lineId: string;
        qty: number;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/transfers/${encodeURIComponent(transferId)}/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async cancelTransfer(
    transferId: string,
    body?: {
      notes?: string;
      referenceType?: string;
      referenceId?: string;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/transfers/${encodeURIComponent(transferId)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getActiveStockCountSession(warehouseId: string) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    qs.set('warehouseId', warehouseId);
    return json(await fetch(`${b}/api/inventory/stock-counts/session?${qs.toString()}`));
  },
  async startStockCountSession(body: {
    warehouseId: string;
    countType?: 'FULL' | 'CYCLE' | 'SPOT';
    title?: string;
    notes?: string;
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getStockCountSessionLines(
    sessionId: string,
    params?: {
      stage?: 'FIRST' | 'RECOUNT';
      blind?: boolean;
      includeExpected?: boolean;
      varianceOnly?: boolean;
      query?: string;
      limit?: number;
    }
  ) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    if (params?.stage) qs.set('stage', params.stage);
    if (typeof params?.blind === 'boolean') qs.set('blind', String(params.blind));
    if (typeof params?.includeExpected === 'boolean')
      qs.set('includeExpected', String(params.includeExpected));
    if (typeof params?.varianceOnly === 'boolean') qs.set('varianceOnly', String(params.varianceOnly));
    if (params?.query) qs.set('query', params.query);
    if (typeof params?.limit === 'number') qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/lines${suffix}`)
    );
  },
  async saveStockCountSession(
    sessionId: string,
    body: {
      stage?: 'FIRST' | 'RECOUNT';
      lines: Array<{
        lineId?: string;
        lineKey?: string;
        sourceType?: 'SNAPSHOT' | 'ADDED';
        countedQty: number;
        countedLocationId?: string | null;
        countedLocationCode?: string | null;
        countedLocationName?: string | null;
        itemId?: string | null;
        sku?: string | null;
        partNumber?: string | null;
        description?: string | null;
        uom?: string | null;
        lotId?: string | null;
        batchLotNumber?: string | null;
        reasonCode?: string | null;
        notes?: string | null;
      }>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async getStockCountSaveJobs(sessionId: string, limit = 20) {
    const b = await getBaseUrl();
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    return json(
      await fetch(
        `${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/jobs?${qs.toString()}`
      )
    );
  },
  async completeStockCountFirst(sessionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/complete-first`, {
        method: 'POST'
      })
    );
  },
  async startStockCountRecount(sessionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/start-recount`,
        {
          method: 'POST'
        }
      )
    );
  },
  async completeStockCountRecount(sessionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(
        `${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/complete-recount`,
        {
          method: 'POST'
        }
      )
    );
  },
  async approveStockCountSession(sessionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/approve`, {
        method: 'POST'
      })
    );
  },
  async cancelStockCountSession(sessionId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/inventory/stock-counts/session/${encodeURIComponent(sessionId)}/cancel`, {
        method: 'POST'
      })
    );
  }
};

export const usersApi = {
  async getMeta() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/users/meta`));
  },
  async getAll() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/users`));
  },
  async getById(id: string) {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/users/${encodeURIComponent(id)}`));
  },
  async getLeaveRequests(options: {
    status?: 'ALL' | 'PENDING' | 'APPROVED' | 'DENIED';
    userId?: string;
    leaveType?: 'ALL' | 'ANNUAL' | 'SICK' | 'OTHER';
    sort?: 'requested_desc' | 'requested_asc' | 'user_asc' | 'start_asc' | 'start_desc';
    summaryOnly?: boolean;
  } = {}) {
    const b = await getBaseUrl();
    const params = new URLSearchParams();
    if (options.status) params.set('status', options.status);
    if (options.userId) params.set('userId', options.userId);
    if (options.leaveType) params.set('leaveType', options.leaveType);
    if (options.sort) params.set('sort', options.sort);
    if (options.summaryOnly) params.set('summaryOnly', '1');
    const query = params.toString();
    return json(await fetch(`${b}/api/users/leave-requests${query ? `?${query}` : ''}`));
  },
  async create(body: {
    username: string;
    email?: string | null;
    fullName?: string | null;
    roleKey: string;
    scopeType?: 'UNIT' | 'GROUP';
    scopeOrgGroupId?: string | null;
    password: string;
    isActive?: boolean;
    customerIds?: string[];
  }) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async update(
    id: string,
    body: {
      username?: string;
      email?: string | null;
      fullName?: string | null;
      roleKey?: string;
      scopeType?: 'UNIT' | 'GROUP';
      scopeOrgGroupId?: string | null;
      isActive?: boolean;
      mustResetPassword?: boolean;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {})
      })
    );
  },
  async resetPassword(id: string, password: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      })
    );
  },
  async replaceCustomerAccess(id: string, customerIds: string[]) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}/customer-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerIds })
      })
    );
  },
  async replacePermissionOverrides(
    id: string,
    overrides: Array<{ permissionKey: string; effect: 'ALLOW' | 'DENY'; reason?: string | null }>
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}/permission-overrides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides })
      })
    );
  },
  async savePlannerWorkspace(
    id: string,
    workspace: {
      plannerProfile?: Record<string, unknown>;
      skillsMatrix?: Record<string, unknown>;
      availability?: Record<string, unknown>;
    }
  ) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}/planner-workspace`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workspace || {})
      })
    );
  },
  async decideLeaveRequest(id: string, requestId: string, decision: 'APPROVED' | 'DENIED', note?: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/users/${encodeURIComponent(id)}/leave-requests/${encodeURIComponent(requestId)}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note: note || '' })
      })
    );
  }
};

export const licensingApi = {
  async getWorkspace() {
    const b = await getBaseUrl();
    return json(await fetch(`${b}/api/licensing/workspace`));
  },
  async assignSeat(entitlementId: string, userId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/licensing/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entitlementId, userId })
      })
    );
  },
  async releaseSeat(assignmentId: string) {
    const b = await getBaseUrl();
    return json(
      await fetch(`${b}/api/licensing/assignments/${encodeURIComponent(assignmentId)}`, {
        method: 'DELETE'
      })
    );
  }
};
